import { Button, ButtonRow } from '@ksyos/design-system';
import { useState } from 'react';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IRequestGetOne } from '../../../../interfaces/requestGetOne';
import { RequestStatus } from '../../../../../../common/enums/requestStatus';
import { ServiceCode } from '../../../../../../common/enums/serviceCode';
import { ToastType } from '../../../../../../common/enums/toastType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { UrlBuilder } from '../../../../../../common/utils/urlBuilder';
import { requestMessageBaseValidationSchema } from '../../../../../../common/validation/requestMessage';
import { postJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { showToast } from '../../../../../utils/showToast';
import { emptyValidationErrors, updateValidationErrorsForOne } from '../../../../../utils/validation';
import { FormDivider } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { FormSection } from '../../../../elements/forms/formSection/FormSection';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { TextAreaInput } from '../../../../fields/TextArea';

interface ISendRequestMessageProps {
    isLastItem: boolean;
    reloadAll: () => void;
    request: IRequestGetOne;
}

export function SendRequestMessage(props: ISendRequestMessageProps) {
    const [message, setMessage] = useState<string | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isSent, setIsSent] = useState(false);

    if (
        !props.request.authorization ||
        !props.request.authorization.canSendMessage ||
        ![RequestStatus.accepted, RequestStatus.requested].includes(props.request.status) ||
        !props.request.requesterPractitioner ||
        !props.isLastItem
    ) {
        return null;
    }

    if (isSent || (props.request.requestMessages && props.request.requestMessages.length > 0)) {
        return null;
    }

    return (
        <>
            <FormDivider></FormDivider>
            <FormSection
                isBorderLess
                title={translateKey('requestMessage.message')}
            >
                <HorizontalFormGroupRead>
                    <FormLabel>{getLabel()}</FormLabel>
                    <InputContainer>
                        <TextAreaInput
                            automation="remarkRequestMessage"
                            error={errors.get('message')}
                            maxLength={1000}
                            onChange={(value) => {
                                setMessage(value);
                                setErrors(emptyValidationErrors);
                            }}
                            placeholder={getPlaceholder()}
                            value={message}
                        />
                    </InputContainer>
                </HorizontalFormGroupRead>

                <ButtonRow justify="right">
                    <Button
                        className="automation-saveSend"
                        isDisabled={!message || errors.size > 0}
                        onClick={saveMessage}
                        type="button"
                        variant="outline"
                    >
                        {translateKey('request.saveSend')}
                    </Button>
                </ButtonRow>
            </FormSection>
        </>
    );

    async function saveMessage() {
        if (message && props.request.requesterPractitioner) {
            const urlBuilder = new UrlBuilder('/api/requestMessages');
            urlBuilder.add('sendMail', true);

            await postJson(urlBuilder.build(), { requestId: props.request.id, message });

            setIsSent(true);
            props.reloadAll();
            showToast(
                ToastType.success,
                translateKey('toast.requestMessageSend', getDisplayName(props.request.requesterPractitioner))
            );
        } else {
            setErrors(updateValidationErrorsForOne(requestMessageBaseValidationSchema, { message }, errors, 'message'));
        }
    }

    function getPlaceholder() {
        if (isGGZCf2f()) {
            return translateKey('requestMessage.notifyWhen');
        }

        return translateKey('requestMessage.messageForRequester');
    }

    function getLabel() {
        if (isGGZCf2f()) {
            return translateKey('requestMessage.appointmentConfirmation');
        }

        return translateKey('requestMessage.confirmation');
    }

    function isGGZCf2f() {
        return props.request.service.code === ServiceCode.GGZCf2f;
    }
}
